import {
  docHeader,
  esc,
  fmtDate,
  openPrintWindow,
  peso,
  type PrintShell,
} from './consignment-print';
import type { StockistSoa } from './api';

/**
 * Printable stockist-consignment paperwork (Track 2A). Deliberately reuses the
 * customer-consignment print module's window, CSS and header rather than
 * copying them — the two document families must look like one set of company
 * paperwork, and a second stylesheet would drift.
 *
 * Naming follows the same rule: CONSIGNMENT NOTE, BILLING STATEMENT, PAYMENT
 * RECORD, STATEMENT OF ACCOUNT — never "Sales Invoice" or "Official Receipt",
 * which are BIR-registered statutory names reserved for the compliance track.
 *
 * All money arrives as decimal strings; arithmetic here is DISPLAY only.
 */

type NoteLike = {
  docNo: string;
  status: string;
  createdAt: string;
  stockistBranchName: string;
  branchName: string;
  memo?: string | null;
  lines: Array<{
    productName: string;
    variantName?: string | null;
    qty: number;
    unitPrice: string;
  }>;
};

export function printStockistConsignmentNote(doc: NoteLike, shell?: PrintShell | null): void {
  let totalUnits = 0;
  let totalValue = 0;
  const rows = doc.lines
    .map((l) => {
      const amount = l.qty * Number(l.unitPrice);
      totalUnits += l.qty;
      totalValue += amount;
      return `<tr>
        <td>${esc(l.productName)}</td>
        <td class="muted">${l.variantName ? esc(l.variantName) : '—'}</td>
        <td class="right">${l.qty}</td>
        <td class="right">${esc(peso(l.unitPrice))}</td>
        <td class="right">${esc(peso(amount))}</td>
      </tr>`;
    })
    .join('');

  const body = `
  ${docHeader('CONSIGNMENT NOTE', doc.docNo, fmtDate(doc.createdAt), doc.status === 'void')}
  <div class="grid">
    <div>
      <p class="label">Consigned to (stockist)</p>
      <p style="font-weight:600;margin:0">${esc(doc.stockistBranchName)}</p>
    </div>
    <div>
      <p class="label">From branch</p>
      <p style="font-weight:600;margin:0">${esc(doc.branchName)}</p>
    </div>
  </div>
  <table>
    <thead><tr>
      <th>Product</th><th>Variation</th>
      <th class="right">Qty</th><th class="right">Billed at</th><th class="right">Amount</th>
    </tr></thead>
    <tbody>${rows}</tbody>
    <tfoot><tr>
      <td colspan="2"></td>
      <td class="right">${totalUnits}</td>
      <td></td>
      <td class="right">${esc(peso(totalValue))}</td>
    </tr></tfoot>
  </table>
  ${doc.memo ? `<p class="muted">${esc(doc.memo)}</p>` : ''}
  <p class="muted" style="margin-top:18px">
    These goods remain company property until sold. This stockist is billed automatically at the
    prices above as the units sell through.
  </p>
  <div class="sign">
    <div><p class="label">Released by</p><div class="line"></div></div>
    <div><p class="label">Received by (stockist)</p><div class="line"></div></div>
  </div>`;
  openPrintWindow(`Consignment Note ${doc.docNo}`, body, shell);
}

type BillingLike = {
  docNo: string;
  status: string;
  invoiceDate: string;
  stockistBranchName: string;
  total: string;
  amountPaid: string;
  lines: Array<{
    productName: string;
    variantName?: string | null;
    qty: number;
    unitPrice: string;
    lineTotal: string;
  }>;
};

export function printStockistBillingStatement(doc: BillingLike, shell?: PrintShell | null): void {
  const rows = doc.lines
    .map(
      (l) => `<tr>
        <td>${esc(l.productName)}</td>
        <td class="muted">${l.variantName ? esc(l.variantName) : '—'}</td>
        <td class="right">${l.qty}</td>
        <td class="right">${esc(peso(l.unitPrice))}</td>
        <td class="right">${esc(peso(l.lineTotal))}</td>
      </tr>`,
    )
    .join('');
  const outstanding = Number(doc.total) - Number(doc.amountPaid);
  const body = `
  ${docHeader('BILLING STATEMENT', doc.docNo, fmtDate(doc.invoiceDate), doc.status === 'void')}
  <div class="grid">
    <div>
      <p class="label">Billed to (stockist)</p>
      <p style="font-weight:600;margin:0">${esc(doc.stockistBranchName)}</p>
    </div>
    <div>
      <p class="label">Status</p>
      <p style="font-weight:600;margin:0">${esc(doc.status)}</p>
    </div>
  </div>
  <table>
    <thead><tr>
      <th>Product</th><th>Variation</th>
      <th class="right">Qty</th><th class="right">Unit</th><th class="right">Amount</th>
    </tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <p style="text-align:right;font-weight:600">Total ${esc(peso(doc.total))}</p>
  ${Number(doc.amountPaid) > 0 ? `<p style="text-align:right" class="muted">Paid ${esc(peso(doc.amountPaid))} · Outstanding ${esc(peso(outstanding))}</p>` : ''}
  <p class="muted" style="margin-top:18px">
    Raised automatically from this stockist's own sale, at the price the goods were consigned at.
  </p>`;
  openPrintWindow(`Billing Statement ${doc.docNo}`, body, shell);
}

type ReceiptLike = {
  docNo: string;
  status: string;
  receivedAt: string;
  stockistBranchName: string;
  amount: string;
  method: string;
  reference: string | null;
  allocations: Array<{ docNo: string; amount: string }>;
};

export function printStockistPaymentRecord(doc: ReceiptLike, shell?: PrintShell | null): void {
  const rows = doc.allocations
    .map(
      (a) => `<tr>
        <td class="code">${esc(a.docNo)}</td>
        <td class="right">${esc(peso(a.amount))}</td>
      </tr>`,
    )
    .join('');
  const body = `
  ${docHeader('PAYMENT RECORD', doc.docNo, fmtDate(doc.receivedAt), doc.status === 'void')}
  <div class="grid">
    <div>
      <p class="label">Received from (stockist)</p>
      <p style="font-weight:600;margin:0">${esc(doc.stockistBranchName)}</p>
    </div>
    <div>
      <p class="label">Method</p>
      <p style="font-weight:600;margin:0">${esc(doc.method.replace('_', ' '))}${
        doc.reference ? ` · ${esc(doc.reference)}` : ''
      }</p>
    </div>
  </div>
  <p style="font-size:20px;font-weight:600;margin:12px 0">${esc(peso(doc.amount))}</p>
  ${
    rows
      ? `<table><thead><tr><th>Applied to</th><th class="right">Amount</th></tr></thead><tbody>${rows}</tbody></table>`
      : ''
  }
  <div class="sign">
    <div><p class="label">Received by</p><div class="line"></div></div>
    <div><p class="label">Paid by (stockist)</p><div class="line"></div></div>
  </div>`;
  openPrintWindow(`Payment Record ${doc.docNo}`, body, shell);
}

export function printStockistSoa(soa: StockistSoa): void {
  const rows = soa.lines
    .map(
      (l) => `<tr>
        <td>${esc(fmtDate(l.date))}</td>
        <td class="code">${esc(l.docNo)}</td>
        <td class="muted">${esc(l.docType)}</td>
        <td class="right">${Number(l.charges) ? esc(peso(l.charges)) : '—'}</td>
        <td class="right">${Number(l.payments) ? esc(peso(l.payments)) : '—'}</td>
        <td class="right">${esc(peso(l.balance))}</td>
      </tr>`,
    )
    .join('');
  const body = `
  ${docHeader('STATEMENT OF ACCOUNT', soa.branch.code, fmtDate(new Date()), false)}
  <div class="grid">
    <div>
      <p class="label">Stockist</p>
      <p style="font-weight:600;margin:0">${esc(soa.branch.name)}</p>
    </div>
    <div>
      <p class="label">Outstanding</p>
      <p style="font-weight:600;margin:0">${esc(peso(soa.closingBalance))}</p>
    </div>
  </div>
  <table>
    <thead><tr>
      <th>Date</th><th>Document</th><th>Type</th>
      <th class="right">Charges</th><th class="right">Payments</th><th class="right">Balance</th>
    </tr></thead>
    <tbody>
      <tr><td colspan="5" class="muted">Opening balance</td><td class="right">${esc(peso(soa.openingBalance))}</td></tr>
      ${rows}
    </tbody>
  </table>
  <p style="text-align:right;font-weight:600">Closing ${esc(peso(soa.closingBalance))}</p>`;
  openPrintWindow(`Statement of Account ${soa.branch.code}`, body);
}
